import axios from 'axios';
import qs from 'query-string';

const defaultTransform = (response) => response?.data;
const getDataFromBodyTransform = (response) => response?.data?.body[0];
const getBodyTransform = (response) => response?.data?.body;

const transformFunctions = {
  defaultTransform,
  getDataFromBodyTransform,
  getBodyTransform,
};

// axios setup
axios.defaults.baseURL = `${process.env.VUE_APP_NEW_BFF_LAYER}`;
axios.defaults.withCredentials = true;

axios.defaults.validateStatus = (status) => {
  return status >= 200 && status < 300;
};

const DEFAULT_RETRIES = 1;

const retryRequest = (requestFunction, retriesLeft = 0) => requestFunction()
  .catch((errResponse) => {
    const { response = {} } = errResponse;
    const { status = 500 } = response;
    if (retriesLeft <= 0 || status < 500) { throw errResponse; } // only retry for 500+ server errors
    return retryRequest(requestFunction, retriesLeft - 1);
  });

class APIRequestService {
  constructor(options = {}) {
    this.axios = options.axios || axios;
  }

  get(url, options = {}, axiosCancelToken = null) {
    // options is a non-axios param that we can use to specify/modify requests
    // options may have a config property that is used for axios configs
    const { config = {}, retries = 1 } = options;
    const extraConfigs = { withCredentials: true };
    if (axiosCancelToken) {
      extraConfigs.cancelToken = axiosCancelToken;
    }
    const requestFunction = () => this.axios.get(url, extraConfigs, config);
    return retryRequest(requestFunction, retries)
      .then(this.getTransformFunction(options.transformFunction));
  }

  post(url, body = {}, options = {}) {
    const { config = {}, retries = DEFAULT_RETRIES } = options;
    const requestFunction = () => this.axios.post(url, qs.stringify(body), config);
    return retryRequest(requestFunction, retries)
      .then(this.getTransformFunction(options.transformFunction));
  }

  uploadFile(url, body = {}, options = {}) {
    const { config = {}, retries = DEFAULT_RETRIES } = options;
    const requestFunction = () => this.axios.post(url, body, config);
    return retryRequest(requestFunction, retries)
      .then(this.getTransformFunction(options.transformFunction));
  }

  put(url, body = {}, options = {}) {
    const { config = {}, retries = DEFAULT_RETRIES } = options;
    const requestFunction = () => this.axios.put(url, qs.stringify(body), config);
    return retryRequest(requestFunction, retries)
      .then(this.getTransformFunction(options.transformFunction));
  }

  patch(url, body = {}, options = {}) {
    const { config = {}, retries = DEFAULT_RETRIES } = options;
    const requestFunction = () => this.axios.patch(url, body, config);
    return retryRequest(requestFunction, retries)
      .then(this.getTransformFunction(options.transformFunction))
      .catch((err) => err); // TODO: handle errors appropriately
  }

  delete(url, options = {}) {
    const { config = {}, retries = DEFAULT_RETRIES } = options;
    const requestFunction = () => this.axios.delete(url, config);
    return retryRequest(requestFunction, retries)
      .then(this.getTransformFunction(options.transformFunction));
  }

  head(...args) {
    const requestFunction = () => this.axios.head(...args);
    return retryRequest(requestFunction, DEFAULT_RETRIES);
  }

  /* eslint-disable */
  getTransformFunction(transformFunction) {
    if (!transformFunction) {
      return defaultTransform;
    }
    if (typeof transformFunction === 'string') {
      return transformFunctions[transformFunction];
    }
    return transformFunction; // in case it is an actual function
  }
}

export default APIRequestService;
